import { create } from 'zustand';
import { StoreApi, UseBoundStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { createFileLogger } from '../../../utils/logger';
import { API_BASE_URL } from '../../../api/config';
import fetchService from '../../../services/fetchService';
import { ContactEntity, ContactInteraction, ContactSkeleton, ContactTier, TIER_CADENCE_DAYS, getWarmthLevel, normalizeTier } from '../types/entity';
import { ContactsAppState, ContactsFilter, ContactsView, SyncLogEntry } from '../types/state';

const log = createFileLogger('CONTACTS_APP');

const TIER_ORDER = ['inner_circle', 'tier1', 'tier2', 'tier3', 'dormant'];

interface ContactsAppStore extends ContactsAppState {
  initialize: (windowId: string, spaceId: string) => Promise<void>;
  cleanup: () => void;

  checkEmailStatus: () => Promise<void>;
  ensureAutomation: () => Promise<void>;

  loadContacts: () => Promise<void>;

  // Navigation
  setView: (view: ContactsView) => void;
  selectContact: (id: string | null) => void;
  setFilter: (filter: Partial<ContactsFilter>) => void;

  // CRUD
  createContact: (data: {
    name: string;
    email?: string;
    tier?: ContactTier;
    company?: string;
    phone?: string;
    notes?: string;
  }) => Promise<ContactEntity | null>;
  updateContact: (id: string, updates: Partial<ContactSkeleton> & { name?: string }) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;

  // Enrichment
  enrichContact: (id: string) => Promise<void>;
  discoverContacts: () => Promise<{ discovered: number; enriched: number }>;
  importFromCsv: (file: File, source?: string) => Promise<{ created: number; updated: number; skipped: number }>;

  // Form
  openForm: (contactId?: string) => void;
  closeForm: () => void;

  // Interactions
  fetchInteractions: (contactId: string) => Promise<void>;
  logInteraction: (contactId: string, data: {
    channel: string;
    direction: string;
    snippet?: string;
    at?: string;
    source?: string;
  }) => Promise<ContactInteraction | null>;
  deleteInteraction: (contactId: string, interactionId: string) => Promise<void>;

  // Sync modal
  appendSyncLog: (entry: Omit<SyncLogEntry, 'id' | 'at'>) => void;
  clearSyncLog: () => void;
  openSyncModal: () => void;
  closeSyncModal: () => void;
  fetchSourcesStatus: () => Promise<void>;

  // Computed
  getFilteredContacts: () => ContactEntity[];
}

type ContactsStoreHook = UseBoundStore<StoreApi<ContactsAppStore>>;

const createContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  return create<ContactsAppStore>()(
    subscribeWithSelector((set, get) => ({
      windowId,
      spaceId,
      isInitialized: false,
      isLoading: false,
      gmailConnected: null,
      contacts: [],
      selectedContactId: null,
      currentView: 'all',
      filter: { search: '', tier: null, tags: [], channel: null, warmth: null, staleOnly: false, identityType: null },
      isFormOpen: false,
      editingContactId: null,
      enrichingContactIds: new Set(),
      isDiscovering: false,
      isImporting: false,
      importResult: null,
      discoverResult: null,
      interactions: {},
      interactionsLoading: new Set(),
      syncLog: [],
      sourcesStatus: null,
      isLoadingSources: false,
      isSyncModalOpen: false,
      isSearchLoading: false,
      searchResults: null,
      searchSnippets: {},
      lastSearchQuery: '',

      initialize: async (winId: string, spcId: string) => {
        set({ isLoading: true, error: undefined });
        log.info('[ContactsApp] Initializing', { windowId: winId, spaceId: spcId });

        try {
          await get().checkEmailStatus();

          if (get().gmailConnected) {
            await get().loadContacts();
            get().ensureAutomation().catch(() => {});
          } else {
            set({ isInitialized: true, isLoading: false });
            return;
          }

          set({ isInitialized: true, isLoading: false });
        } catch (error) {
          log.error('[ContactsApp] Initialization failed', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize',
            isLoading: false,
            isInitialized: true,
          });
        }
      },

      cleanup: () => {
        set({ contacts: [], isInitialized: false });
        storeMap.delete(`${windowId}-${spaceId}`);
      },

      checkEmailStatus: async () => {
        try {
          const result = await fetchService(`${API_BASE_URL}/contacts-app/email-status`);
          set({ gmailConnected: result?.connected === true });
        } catch (error) {
          log.error('[ContactsApp] checkEmailStatus failed', error);
          set({ gmailConnected: false });
        }
      },

      ensureAutomation: async () => {
        try {
          await fetchService(`${API_BASE_URL}/contacts-app/ensure-automation`, { method: 'POST' });
        } catch (error) {
          log.error('[ContactsApp] ensureAutomation failed', error);
        }
      },

      loadContacts: async () => {
        try {
          const raw: any[] = await fetchService(`${API_BASE_URL}/contacts-app`);
          // Normalize legacy tier values on load
          const contacts = (raw || []).map((c) => ({
            ...c,
            skeleton: { ...c.skeleton, tier: normalizeTier(c.skeleton?.tier) },
          }));
          set({ contacts });
        } catch (error) {
          log.error('[ContactsApp] Failed to load contacts', error);
          set({ error: 'Failed to load contacts' });
        }
      },

      setView: (view) => {
        set((state) => ({
          currentView: view,
          selectedContactId: null,
          filter: { ...state.filter, tier: null },
        }));
      },

      selectContact: (id) => {
        set({ selectedContactId: id });
      },

      setFilter: (filter) => {
        set((state) => ({ filter: { ...state.filter, ...filter } }));
      },

      createContact: async (data) => {
        try {
          const contact = await fetchService(`${API_BASE_URL}/contacts-app`, {
            method: 'POST',
            body: JSON.stringify(data),
          });
          const normalized = { ...contact, skeleton: { ...contact.skeleton, tier: normalizeTier(contact.skeleton?.tier) } };
          set((state) => ({ contacts: [...state.contacts, normalized] }));
          log.info('[ContactsApp] Contact created', contact._id);
          return normalized;
        } catch (error) {
          log.error('[ContactsApp] createContact failed', error);
          return null;
        }
      },

      updateContact: async (id, updates) => {
        try {
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
          });
          const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
          set((state) => ({
            contacts: state.contacts.map((c) => (c._id === id ? normalized : c)),
          }));
        } catch (error) {
          log.error('[ContactsApp] updateContact failed', error);
        }
      },

      deleteContact: async (id) => {
        try {
          await fetchService(`${API_BASE_URL}/contacts-app/${id}`, { method: 'DELETE' });
          set((state) => ({
            contacts: state.contacts.filter((c) => c._id !== id),
            selectedContactId: state.selectedContactId === id ? null : state.selectedContactId,
          }));
        } catch (error) {
          log.error('[ContactsApp] deleteContact failed', error);
        }
      },

      enrichContact: async (id) => {
        set((state) => {
          const next = new Set(state.enrichingContactIds);
          next.add(id);
          return { enrichingContactIds: next };
        });

        try {
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${id}/enrich`, {
            method: 'POST',
          });
          const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
          set((state) => ({
            contacts: state.contacts.map((c) => (c._id === id ? normalized : c)),
            enrichingContactIds: (() => {
              const next = new Set(state.enrichingContactIds);
              next.delete(id);
              return next;
            })(),
          }));
          log.info('[ContactsApp] Enriched contact', id);
        } catch (error) {
          log.error('[ContactsApp] enrichContact failed', error);
          set((state) => {
            const next = new Set(state.enrichingContactIds);
            next.delete(id);
            return { enrichingContactIds: next };
          });
        }
      },

      discoverContacts: async () => {
        set({ isDiscovering: true, discoverResult: null });
        try {
          const result = await fetchService(`${API_BASE_URL}/contacts-app/discover`, {
            method: 'POST',
          });
          await get().loadContacts();
          const discovered = result?.discovered ?? 0;
          set({ isDiscovering: false, discoverResult: { discovered } });
          setTimeout(() => set({ discoverResult: null }), 5000);
          log.info('[ContactsApp] Discovery complete', result);
          return result;
        } catch (error) {
          log.error('[ContactsApp] discoverContacts failed', error);
          set({ isDiscovering: false });
          return { discovered: 0, enriched: 0 };
        }
      },

      importFromCsv: async (file: File, source = 'csv') => {
        set({ isImporting: true, importResult: null });
        log.info('[ContactsApp] Starting CSV import', { fileName: file.name, source });

        try {
          const csvContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string ?? '');
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file, 'utf-8');
          });

          const result = await fetchService(`${API_BASE_URL}/tools/execute`, {
            method: 'POST',
            body: JSON.stringify({
              toolName: 'contacts_import_csv',
              params: { csvContent, source, defaultTier: 'regular' },
              spaceId: get().spaceId,
            }),
          });

          if (result.status === 'error') {
            throw new Error(result.error ?? 'Import failed');
          }

          const { created = 0, updated = 0, skipped = 0 } = result.data ?? result;
          log.info('[ContactsApp] CSV import complete', { created, updated, skipped });

          await get().loadContacts();
          set({ isImporting: false, importResult: { created, updated, skipped } });

          setTimeout(() => set({ importResult: null }), 5000);

          return { created, updated, skipped };
        } catch (error) {
          log.error('[ContactsApp] importFromCsv failed', error);
          set({ isImporting: false });
          throw error;
        }
      },

      openForm: (contactId) => {
        set({ isFormOpen: true, editingContactId: contactId || null });
      },

      closeForm: () => {
        set({ isFormOpen: false, editingContactId: null });
      },

      fetchInteractions: async (contactId: string) => {
        set((state) => {
          const next = new Set(state.interactionsLoading);
          next.add(contactId);
          return { interactionsLoading: next };
        });
        try {
          const result = await fetchService(
            `${API_BASE_URL}/contacts-app/${contactId}/interactions?limit=20`
          );
          const items: ContactInteraction[] = result?.interactions ?? [];
          set((state) => ({
            interactions: { ...state.interactions, [contactId]: items },
            interactionsLoading: (() => {
              const next = new Set(state.interactionsLoading);
              next.delete(contactId);
              return next;
            })(),
          }));
        } catch (error) {
          log.error('[ContactsApp] fetchInteractions failed', error);
          set((state) => {
            const next = new Set(state.interactionsLoading);
            next.delete(contactId);
            return { interactionsLoading: next };
          });
        }
      },

      logInteraction: async (contactId: string, data) => {
        try {
          const interaction = await fetchService(
            `${API_BASE_URL}/contacts-app/${contactId}/interactions`,
            {
              method: 'POST',
              body: JSON.stringify({
                channel: data.channel,
                direction: data.direction,
                snippet: data.snippet,
                at: data.at,
                source: data.source ?? 'manual',
              }),
            }
          );
          set((state) => ({
            interactions: {
              ...state.interactions,
              [contactId]: [interaction, ...(state.interactions[contactId] ?? [])],
            },
          }));
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${contactId}`);
          if (updated) {
            const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
            set((state) => ({
              contacts: state.contacts.map((c) => (c._id === contactId ? normalized : c)),
            }));
          }
          return interaction;
        } catch (error) {
          log.error('[ContactsApp] logInteraction failed', error);
          get().appendSyncLog({
            operation: 'log_interaction',
            status: 'error',
            message: `Interaction log failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
            contactId,
          });
          return null;
        }
      },

      deleteInteraction: async (contactId: string, interactionId: string) => {
        try {
          await fetchService(
            `${API_BASE_URL}/contacts-app/${contactId}/interactions/${interactionId}`,
            { method: 'DELETE' }
          );
          set((state) => ({
            interactions: {
              ...state.interactions,
              [contactId]: (state.interactions[contactId] ?? []).filter(
                (i) => i._id !== interactionId
              ),
            },
          }));
          const updated = await fetchService(`${API_BASE_URL}/contacts-app/${contactId}`);
          if (updated) {
            const normalized = { ...updated, skeleton: { ...updated.skeleton, tier: normalizeTier(updated.skeleton?.tier) } };
            set((state) => ({
              contacts: state.contacts.map((c) => (c._id === contactId ? normalized : c)),
            }));
          }
        } catch (error) {
          log.error('[ContactsApp] deleteInteraction failed', error);
        }
      },

      appendSyncLog: (entry) => {
        const full: SyncLogEntry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          at: new Date().toISOString(),
          ...entry,
        };
        set((state) => ({ syncLog: [...state.syncLog, full] }));
      },

      clearSyncLog: () => {
        set({ syncLog: [] });
      },

      openSyncModal: () => {
        set({ isSyncModalOpen: true });
      },

      closeSyncModal: () => {
        set({ isSyncModalOpen: false });
      },

      fetchSourcesStatus: async () => {
        set({ isLoadingSources: true });
        try {
          const res = await fetchService(`${API_BASE_URL}/contacts-app/sources`, { method: 'GET' });
          set({ sourcesStatus: res, isLoadingSources: false });
        } catch (err) {
          log.error('[ContactsApp] Failed to fetch sources status', err);
          set({ isLoadingSources: false });
        }
      },

      getFilteredContacts: () => {
        const { contacts, currentView, filter, searchResults } = get();
        const now = Date.now();
        const usingBackendResults = searchResults !== null && filter.search.length >= 3;

        let result = usingBackendResults ? searchResults! : contacts;

        const tierViews = ['inner_circle', 'tier1', 'tier2', 'tier3', 'dormant'];
        if (tierViews.includes(currentView)) {
          result = result.filter((c) => c.skeleton.tier === currentView);
        } else if (currentView === 'stale') {
          result = result.filter((c) => {
            const cadence = TIER_CADENCE_DAYS[c.skeleton.tier];
            if (!cadence || !c.skeleton.lastInteraction?.at) return !!cadence;
            return (now - new Date(c.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
          });
        } else if (currentView === 'hot') {
          result = result.filter((c) => (c.skeleton.warmthScore ?? 0) > 0.7);
        } else if (currentView === 'reddit') {
          result = result.filter((c) => !!(c.skeleton.handles?.reddit));
        } else if (currentView === 'twitter') {
          result = result.filter((c) => !!(c.skeleton.handles?.twitter || c.skeleton.twitterHandle));
        } else if (currentView === 'slack') {
          result = result.filter((c) => !!(c.skeleton.handles?.slack));
        } else if (currentView === 'gmail') {
          result = result.filter((c) => c.skeleton.sources?.includes('gmail'));
        } else if (currentView === 'calendar') {
          result = result.filter((c) => c.skeleton.sources?.includes('calendar'));
        } else if (currentView === 'google_contacts') {
          result = result.filter((c) => c.skeleton.sources?.includes('google_contacts'));
        } else if (currentView === 'linkedin') {
          result = result.filter((c) => c.skeleton.sources?.includes('linkedin'));
        } else if (currentView === 'people') {
          result = result.filter((c) => !c.skeleton.identityType || c.skeleton.identityType === 'person');
        } else if (currentView === 'accounts') {
          result = result.filter((c) => c.skeleton.identityType === 'account');
        } else if (currentView === 'recently_added') {
          const cutoff = Date.now() - 30 * 86400000;
          result = result.filter((c) => new Date(c.created_at ?? 0).getTime() > cutoff);
        } else if (currentView === 'no_email') {
          result = result.filter((c) => !c.skeleton.email);
        }

        if (filter.tier) {
          result = result.filter((c) => c.skeleton.tier === filter.tier);
        }
        if (filter.channel) {
          result = result.filter((c) => c.skeleton.primaryChannel === filter.channel);
        }
        if (filter.warmth) {
          result = result.filter((c) => getWarmthLevel(c.skeleton.warmthScore ?? 0) === filter.warmth);
        }
        if (filter.staleOnly) {
          result = result.filter((c) => {
            const cadence = TIER_CADENCE_DAYS[c.skeleton.tier];
            if (!cadence || !c.skeleton.lastInteraction?.at) return !!cadence;
            return (now - new Date(c.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
          });
        }
        if (filter.identityType) {
          result = result.filter((c) => c.skeleton.identityType === filter.identityType);
        }

        if (filter.search && !usingBackendResults) {
          const q = filter.search.toLowerCase();
          result = result.filter((c) => {
            const sk = c.skeleton;
            return (
              c.name.toLowerCase().includes(q) ||
              sk.email?.toLowerCase().includes(q) ||
              sk.company?.toLowerCase().includes(q) ||
              sk.title?.toLowerCase().includes(q) ||
              sk.notes?.toLowerCase().includes(q) ||
              sk.phone?.toLowerCase().includes(q) ||
              sk.primaryChannelContext?.toLowerCase().includes(q) ||
              sk.handles?.reddit?.toLowerCase().includes(q) ||
              sk.handles?.twitter?.toLowerCase().includes(q) ||
              sk.handles?.slack?.toLowerCase().includes(q) ||
              sk.handles?.github?.toLowerCase().includes(q) ||
              sk.twitterHandle?.toLowerCase().includes(q) ||
              sk.githubHandle?.toLowerCase().includes(q) ||
              sk.tags?.some((t) => t.toLowerCase().includes(q)) ||
              sk.topics?.some((t) => t.toLowerCase().includes(q))
            );
          });
        }

        if (filter.tags.length > 0) {
          result = result.filter((c) =>
            filter.tags.every((tag) => c.skeleton.tags?.includes(tag))
          );
        }

        if (usingBackendResults) return result;

        return result.sort((a, b) => {
          const aTier = TIER_ORDER.indexOf(a.skeleton.tier);
          const bTier = TIER_ORDER.indexOf(b.skeleton.tier);
          if (aTier !== bTier) return aTier - bTier;
          return a.name.localeCompare(b.name);
        });
      },
    }))
  );
};

const storeMap = new Map<string, ContactsStoreHook>();

export const getContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  const key = `${windowId}-${spaceId}`;
  if (!storeMap.has(key)) {
    storeMap.set(key, createContactsAppStore(windowId, spaceId));
  }
  return storeMap.get(key)!;
};

export const useContactsAppStore = (windowId: string, spaceId: string): ContactsStoreHook => {
  return getContactsAppStore(windowId, spaceId);
};
